import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { adminVehiclesAPI } from '../../services/api';
import AdminVehicleAlertsCard from '../AdminVehicleAlertsCard';
import AdminNewArrivalsSubsCard from '../AdminNewArrivalsSubsCard';
import VehicleImageAiCard from '../VehicleImageAiCard';
import UnmatchedFitmentsPanel from '../UnmatchedFitmentsPanel';
import VehicleModelSeoPanel from '../VehicleModelSeoPanel';
import ShareStatsCard from '../ShareStatsCard';
import ShareAttributionTable from '../ShareAttributionTable';
import VehicleBrandsManager from '../VehicleBrandsManager';
import VehicleAddForm from '../VehicleAddForm';
import VehicleImportModal from '../VehicleImportModal';
import VehicleEnginesPasteModal from '../VehicleEnginesPasteModal';
import AdminVehicleBrandList from './AdminVehicleBrandList';
import AdminVehicleDetails from './AdminVehicleDetails';

const AdminVehiclesTab = ({ onError }) => {
  const [vehiclesData, setVehiclesData] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'paste' | 'import'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const close = () => setModal(null);

  const loadVehicles = async () => {
    try {
      setVehiclesData(await adminVehiclesAPI.getAll());
    } catch (error) {
      console.error('Error loading vehicles:', error);
      onError(error);
    }
  };
  useEffect(() => { loadVehicles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await loadVehicles(); } finally { setIsRefreshing(false); }
  };

  const viewDetails = async (brand, model) => {
    try {
      const details = await adminVehiclesAPI.getDetails(brand, model);
      setVehicleDetails(details);
      setSelectedVehicle({ brand, model });
    } catch (error) {
      console.error('Error loading vehicle details:', error);
    }
  };

  const handleDelete = async (brand, model) => {
    if (!window.confirm(`Supprimer ${brand} ${model} ?`)) return;
    try {
      await adminVehiclesAPI.delete(brand, model);
      await loadVehicles();
      setSelectedVehicle(null);
      setVehicleDetails(null);
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const reloadCurrent = () => Promise.all([loadVehicles(), viewDetails(vehicleDetails.brand, vehicleDetails.model)]);
  const existingBrands = vehiclesData ? Object.keys(vehiclesData.brands) : [];

  return (
    <>
      <AdminVehicleAlertsCard />
      <AdminNewArrivalsSubsCard />
      <VehicleImageAiCard />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Véhicules</h1>
          {vehiclesData && <p className="text-sm text-gray-500 mt-1">{vehiclesData.total_brands} marques • {vehiclesData.total_vehicles} modèles</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="flex items-center gap-2">
            <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
            {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
          </Button>
          <Button variant="outline" onClick={() => setModal('import')} className="flex items-center gap-2">
            <Upload size={18} />
            Importer
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setModal('add')}>
            <Plus size={18} className="mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      <UnmatchedFitmentsPanel onChanged={loadVehicles} />
      <VehicleModelSeoPanel />
      <ShareStatsCard />
      <ShareAttributionTable withReferral testId="share-attribution" />
      <VehicleBrandsManager onChanged={loadVehicles} logos={vehiclesData?.logos || {}} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AdminVehicleBrandList vehiclesData={vehiclesData} expandedBrand={expandedBrand} setExpandedBrand={setExpandedBrand} selectedVehicle={selectedVehicle} onSelect={viewDetails} />
        <AdminVehicleDetails
          selectedVehicle={selectedVehicle} vehicleDetails={vehicleDetails}
          onPasteEngines={() => setModal('paste')} onEdit={() => setModal('edit')} onDelete={handleDelete}
          onImageChanged={(img) => setVehicleDetails(d => ({ ...d, image: img || '' }))}
        />
      </div>

      {modal === 'paste' && vehicleDetails && (
        <VehicleEnginesPasteModal brand={vehicleDetails.brand} model={vehicleDetails.model} onClose={close} onImported={reloadCurrent} />
      )}
      {modal === 'edit' && vehicleDetails && (
        <VehicleAddForm
          initialData={vehicleDetails}
          onClose={close}
          onSuccess={async (vehicleData) => {
            await adminVehiclesAPI.update(vehicleDetails.brand, vehicleDetails.model, { years: vehicleData.years, engines: vehicleData.engines });
            await reloadCurrent();
          }}
          existingBrands={existingBrands}
        />
      )}
      {modal === 'add' && (
        <VehicleAddForm
          onClose={close}
          onSuccess={async (vehicleData) => {
            await adminVehiclesAPI.add(vehicleData);
            await loadVehicles();
            alert(`Véhicule ${vehicleData.brand} ${vehicleData.model} ajouté avec succès !`);
          }}
          existingBrands={existingBrands}
        />
      )}
      <VehicleImportModal isOpen={modal === 'import'} onClose={close} onSuccess={loadVehicles} />
    </>
  );
};

export default AdminVehiclesTab;
